/**
 * components/clients/PreviousValuePopover.tsx
 *
 * Displayed under a field when a previous value exists.
 * Two variants:
 *   - 'merge' (default): shown after a client merge. Style: amber (notable event).
 *     Props: mergedAt, sourceClientName.
 *   - 'resubmission': shown when a client re-submitted a form with changed values.
 *     Style: neutral/discreet (historical reference, not an alert).
 *     Props: history (ordered array of previous values, newest-first).
 *     Supports multiple historical entries (one per re-submission where value changed).
 * Created Apr 2026.
 * Fix Apr 2026: added type="button" to the toggle to prevent accidental form submissions.
 * Updated Apr 2026: added 'resubmission' variant with neutral style and multi-entry history.
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp, History } from 'lucide-react';
import { formatDateWithTime } from '../../utils/dateUtils';

interface MergeVariantProps {
  variant?: 'merge';
  previousValue: string | null;
  mergedAt: string;
  sourceClientName: string;
}

interface ResubmissionVariantProps {
  variant: 'resubmission';
  history: Array<{ value: string | null; submittedAt: string | null }>;
}

type PreviousValuePopoverProps = MergeVariantProps | ResubmissionVariantProps;

export default function PreviousValuePopover(props: PreviousValuePopoverProps) {
  const [open, setOpen] = useState(false);

  if (props.variant === 'resubmission') {
    const { history } = props;
    const label = history.length > 1
      ? `Valeurs précédentes (${history.length})`
      : 'Valeur précédente';

    return (
      <div className="mt-1">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
        >
          <History className="w-3 h-3" />
          <span>{label}</span>
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>

        {open && (
          <div className="mt-1.5 bg-neutral-50 border border-neutral-200 rounded-lg overflow-hidden">
            <div className="divide-y divide-neutral-100">
              {history.map((entry, idx) => (
                <div key={idx} className="flex items-baseline justify-between gap-3 px-2.5 py-1.5">
                  <span className="text-xs text-neutral-700 min-w-0 truncate">
                    {entry.value || <span className="italic text-neutral-400">Vide</span>}
                  </span>
                  {entry.submittedAt && (
                    <span className="text-[11px] text-neutral-400 flex-shrink-0">
                      {formatDateWithTime(entry.submittedAt)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // merge variant — keep existing amber style
  const formattedDate = new Date(props.mergedAt).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
      >
        <History className="w-3 h-3" />
        <span>Valeur précédente</span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && (
        <div className="mt-1.5 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs">
          <p className="text-amber-700 font-medium mb-0.5">
            {props.previousValue || <span className="italic">Vide</span>}
          </p>
          <p className="text-amber-500">
            Remplacée le {formattedDate} (fusion avec {props.sourceClientName})
          </p>
        </div>
      )}
    </div>
  );
}
